'use client';

import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, MessageCircle, X, Handshake } from 'lucide-react';
import confetti from 'canvas-confetti';
import { Button } from '@/components/ui/button';

interface MatchModalProps {
  job: {
    title: string;
    company: string;
  };
  onClose: () => void;
  onStartChat: () => void;
}

export function MatchModal({ job, onClose, onStartChat }: MatchModalProps) {
  // Trigger confetti on mount
  useEffect(() => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 100 };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    const interval: NodeJS.Timeout = setInterval(function () {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      // Confetti from left
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#10B981', '#059669', '#34D399', '#6EE7B7'],
      });

      // Confetti from right
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#10B981', '#059669', '#34D399', '#6EE7B7'],
      });
    }, 250);

    return () => clearInterval(interval);
  }, []);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pb-24">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 backdrop-blur-md"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300,
          }}
          className="relative w-full max-w-sm rounded-3xl p-8"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-floating)',
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full transition-colors"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Celebration icon with pulse animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              type: 'spring',
              damping: 15,
              stiffness: 300,
              delay: 0.2,
            }}
            className="flex justify-center mb-6"
          >
            <div className="relative">
              {/* Glow effect */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="absolute inset-0 rounded-full blur-2xl"
                style={{
                  backgroundColor: 'var(--color-bg-accent)',
                }}
              />
              {/* Icon container */}
              <div
                className="relative flex items-center justify-center w-28 h-28 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  boxShadow: '0 20px 60px rgba(16, 185, 129, 0.4)',
                }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{
                    duration: 0.5,
                    repeat: Infinity,
                    repeatDelay: 1,
                  }}
                >
                  <Handshake className="w-14 h-14 text-white" />
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl font-bold text-center mb-2"
            style={{
              color: 'var(--color-text-primary)',
              fontWeight: 'var(--font-weight-bold)',
            }}
          >
            Ny forbindelse! 🎉
          </motion.h2>

          {/* Job info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-6"
          >
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-body-md)' }} className="mb-1">
              Du og
            </p>
            <p
              className="text-xl font-semibold"
              style={{
                color: 'var(--color-text-primary)',
                fontWeight: 'var(--font-weight-semibold)',
              }}
            >
              {job.company}
            </p>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-body-md)' }} className="mt-1">
              er gjensidig interessert i
            </p>
            <p
              className="text-base font-medium mt-1"
              style={{
                color: 'rgb(16, 185, 129)',
                fontWeight: 'var(--font-weight-semibold)',
              }}
            >
              {job.title}
            </p>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mb-8"
            style={{
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-body-md)',
              lineHeight: '1.5',
            }}
          >
            Dere kan nå chatte anonymt og lære mer om hverandre før dere eventuelt avslører identitet.
          </motion.p>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="space-y-3"
          >
            <Button onClick={onStartChat} className="w-full" size="lg">
              <MessageCircle className="w-5 h-5 mr-2" />
              Send melding
            </Button>
            <Button onClick={onClose} variant="secondary" className="w-full" size="lg">
              Fortsett å swipe
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
